import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useVenue } from '../../contexts/VenueContext';
import { Package } from '../../types/Venue';
import { createPackage } from '../../services/venueService';

// Import form components
import PackageBasicInfoForm from './components/PackageBasicInfoForm';
import PackagePricingForm from './components/PackagePricingForm';
import PackageIncludedItemsForm from './components/PackageIncludedItemsForm';
import PackageAvailabilityForm from './components/PackageAvailabilityForm';
import PackageImagesForm from './components/PackageImagesForm';

const steps = [
  'Basic Info',
  'Pricing',
  'Included Items',
  'Availability',
  'Images'
];

const initialPackageState: Omit<Package, 'id'> = {
  name: '',
  description: '',
  packageType: 'dinner', // default type
  price: 0,
  discountedPrice: undefined,
  images: [],
  includesItems: [],
  availability: {
    startDate: undefined,
    endDate: undefined,
    daysAvailable: [0, 1, 2, 3, 4, 5, 6], // All days by default
    timeSlots: [{
      start: '18:00',
      end: '20:00'
    }]
  },
  maxCapacity: 2, // Default for a date package
  restrictions: [],
  isActive: false,
  createdAt: null,
  updatedAt: null,
  venueId: ''
};

const CreatePackage: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const { activeVenue, setActiveVenue, isLoading, error } = useVenue();
  
  const [activeStep, setActiveStep] = useState(0);
  const [packageData, setPackageData] = useState<Omit<Package, 'id'>>({
    ...initialPackageState,
    venueId: venueId || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (venueId) {
      setActiveVenue(venueId);
      setPackageData(prev => ({
        ...prev,
        venueId
      }));
    }
  }, [venueId, setActiveVenue]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFormChange = (data: Partial<Package>) => {
    setPackageData((prev) => ({
      ...prev,
      ...data
    }));
  };

  const validateCurrentStep = (): boolean => {
    switch (activeStep) {
      case 0: // Basic Info
        if (!packageData.name || !packageData.description || !packageData.packageType) {
          setFormError('Please fill in all required fields');
          return false;
        }
        break;
        
      case 1: // Pricing
        if (packageData.price <= 0) {
          setFormError('Price must be greater than zero');
          return false;
        }
        break;
        
      case 2: // Included Items
        if (packageData.includesItems.length === 0) {
          setFormError('Please add at least one included item');
          return false;
        }
        break;
        
      case 3: // Availability
        if (packageData.availability.timeSlots.length === 0) {
          setFormError('Please add at least one time slot');
          return false;
        }
        break;
        
      // Images step doesn't require validation
    }
    
    setFormError(null);
    return true;
  };

  const handleStepValidation = () => {
    if (validateCurrentStep()) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        handleNext();
      }
    }
  };

  const handleSubmit = async () => {
    if (!venueId) {
      setFormError('Venue ID is missing');
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      await createPackage(venueId, packageData);
      navigate(`/venues/${venueId}`);
    } catch (error) {
      console.error('Error creating package:', error);
      setFormError('Failed to create package. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PackageBasicInfoForm 
            packageData={packageData} 
            onChange={handleFormChange} 
          />
        );
      case 1:
        return (
          <PackagePricingForm 
            packageData={packageData} 
            onChange={handleFormChange} 
          />
        );
      case 2:
        return (
          <PackageIncludedItemsForm 
            packageData={packageData} 
            onChange={handleFormChange} 
          />
        );
      case 3:
        return (
          <PackageAvailabilityForm 
            packageData={packageData} 
            onChange={handleFormChange} 
          />
        );
      case 4:
        return (
          <PackageImagesForm 
            packageData={packageData} 
            onChange={handleFormChange} 
            venueId={venueId || ''}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(`/venues/${venueId}`)}
          sx={{ mt: 2 }}
        >
          Back to Venue
        </Button>
      </Container>
    );
  }

  if (!activeVenue) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Venue not found</Alert>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/venues')}
          sx={{ mt: 2 }}
        >
          Back to Venues
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(`/venues/${venueId}`)}
          >
            Back to {activeVenue.name}
          </Button>
        </Box>

        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create Package
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
          Create a new date package for {activeVenue.name}
        </Typography>

        <Stepper activeStep={activeStep} sx={{ py: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {formError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {formError}
          </Alert>
        )}

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            disabled={activeStep === 0 || submitting}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStepValidation}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 1 ? (
              'Create Package'
            ) : (
              'Next'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CreatePackage;
